import React, { useState, useEffect } from 'react';
import { setExceptionHandler, removeDetailsBreadcrumb, addDetailsBreadcrumb } from '@app/store/actions';
import { useTypedTranslation } from '@app/hooks';
import { useDispatch } from 'react-redux';
import { parse } from 'qs';
import { useParams, useLocation } from 'react-router';
import { scheduledJobsService } from '@app/services';
import { genericErrorToast, passlyFormatDate, TIME_DATE_FORMAT } from '@app/helpers';
import { IScheduledJobs, TranslationsTypes, IScheduledJobsTriggers } from '@app/models';
import { AxiosError } from 'axios';
import { PageLoader, PasslyButton, ModalComponent } from '@app/components';
import { TriggerJobDialog } from '@app/pages/systemManager/dialogs/triggerJob.dialog';

interface IJobDisplayData {
    jobName: string;
    group: string;
    description: string;
    jobClass: string;
}

interface IJobTriggerDisplayData {
    triggerType: string;
    previousFireTime: Date;
    nextFireTime: Date;
    triggerState: string;
    fulljson: string;
}

const ScheduledJobsDetailsPage = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const { translation } = useTypedTranslation();

    const { id: jobId } = useParams<{ id: string }>();
    const queryParams = parse(location.search, { ignoreQueryPrefix: true });

    const jobGroup: string = String(queryParams.group);
    const jobScheduler: string = String(queryParams.scheduler);

    const [jobDetails, setJobDetails] = useState<IScheduledJobs>();
    const [jobDisplay, setJobDisplay] = useState<IJobDisplayData>();
    const [jobTriggerDisplay, setJobTriggerDisplay] = useState<IJobTriggerDisplayData[]>([]);
    const [loadingData, setLoadingData] = useState<boolean>(true);

    const [openTriggerJob, setOpenTriggerJob] = useState<boolean>(false);

    useEffect(() => {
        dispatch(setExceptionHandler(true));
        dispatch(addDetailsBreadcrumb(jobId));

        fetchData();

        return () => {
            dispatch(removeDetailsBreadcrumb());
        };
    }, []);

    const fetchData = () => {
        setLoadingData(true);

        scheduledJobsService.getScheduledJob(jobId, jobGroup, jobScheduler, handleSuccessGet, handleError);
    };

    const handleSuccessGet = (response: any) => {
        setLoadingData(false);
        const result: IScheduledJobs = response.data;

        setJobDetails(result);

        let resultJob: IJobDisplayData = {} as IJobDisplayData;
        resultJob.description = result.Description;
        resultJob.group = result.JobGroup;
        resultJob.jobClass = result.ClassName;
        resultJob.jobName = result.JobName;

        setJobDisplay(resultJob);

        if (result.Triggers.length > 0) {
            let resultTriggers: IJobTriggerDisplayData[] = [];

            result.Triggers.forEach(function (trig: IScheduledJobsTriggers) {
                let resultTrigger: IJobTriggerDisplayData = {} as IJobTriggerDisplayData;
                resultTrigger.fulljson = JSON.stringify(trig, null, 2);
                resultTrigger.nextFireTime = trig.NextFireTime;
                resultTrigger.previousFireTime = trig.PreviousFireTime;
                resultTrigger.triggerState = trig.State;
                resultTrigger.triggerType = translation(trig.Type as TranslationsTypes);

                resultTriggers.push(resultTrigger);
            });

            setJobTriggerDisplay(resultTriggers);
        }
    };

    const handleError = (response: AxiosError<any>) => {
        setLoadingData(false);

        genericErrorToast(response);
    };

    const triggerJobNow = () => {
        setOpenTriggerJob(true);
    };

    const handleModalCloseCallback = (resetPage: boolean) => {
        setOpenTriggerJob(!openTriggerJob);

        if (resetPage) {
            handleSuccessTriggered();
        }
    };

    const handleSuccessTriggered = () => {
        fetchData();
    };

    return (
        <>
            <ModalComponent
                headerTitle={translation('TRIGGER_JOB')}
                modalSwitcherCallback={handleModalCloseCallback}
                form={<TriggerJobDialog dialogOpen={handleModalCloseCallback} jobDetails={jobDetails!} jobId={jobId} jobScheduler={jobScheduler} />}
                localModalSwitcher={openTriggerJob}
            />

            {loadingData && <PageLoader />}

            {!loadingData &&
                <>
                    <div className='content--narrow detailPage'>
                        <div className='detailPage__section'>
                            <div className='detailPage__section-head'>
                                {translation('JOB_DETAILS')}
                            </div>

                            <div className='detailPage__section-content'>
                                <ul className='detailPage__list'>
                                    <li className='detailPage__list-subhead'>
                                        <span className='detailPage__list--primary-label detailPage__section-subhead'>{translation('BASIC_INFORMATION')}</span>
                                    </li>
                                    <li>
                                        <span className='detailPage__list--primary-label'>{translation('JOB_NAME')}</span>
                                        <span className='detailPage__list--secondary-label'>{jobDisplay?.jobName}</span>
                                    </li>
                                    <li>
                                        <span className='detailPage__list--primary-label'>{translation('GROUP')}</span>
                                        <span className='detailPage__list--secondary-label'>{jobDisplay?.group}</span>
                                    </li>
                                    <li>
                                        <span className='detailPage__list--primary-label'>{translation('DESCRIPTION')}</span>
                                        <span className='detailPage__list--secondary-label'>{jobDisplay?.description}</span>
                                    </li>
                                    <li>
                                        <span className='detailPage__list--primary-label'>{translation('JOB_CLASS')}</span></li><li>
                                        <span>
                                            <code>{jobDisplay?.jobClass}</code>
                                        </span>
                                    </li>

                                    <li>
                                        <span className='detailPage__list--primary-label detailPage__section-subhead'>{translation('JOB_TRIGGERS')}</span>
                                    </li>
                                    <li>
                                        <span className='detailPage__list--primary-label'>
                                            <PasslyButton id='triggerJobButton' className='modal__actions--primary' variant='contained' color='primary' onClick={triggerJobNow}>
                                                {translation('QUEUE_TRIGGER')}
                                            </PasslyButton>
                                        </span>
                                    </li>
                                    {jobTriggerDisplay.map((result, index) => (
                                        <>
                                            <li>
                                                <span className='detailPage__list--primary-label'>{translation('TYPE')}</span>
                                                <span className='detailPage__list--secondary-label'>{result.triggerType}</span>
                                            </li>
                                            <li>
                                                <span className='detailPage__list--primary-label'>{translation('LAST')}</span>
                                                <span className='detailPage__list--secondary-label'>
                                                    {passlyFormatDate(result.previousFireTime, TIME_DATE_FORMAT)}
                                                </span>
                                            </li>
                                            <li>
                                                <span className='detailPage__list--primary-label'>{translation('NEXT')}</span>
                                                <span className='detailPage__list--secondary-label'>
                                                    {passlyFormatDate(result.nextFireTime, TIME_DATE_FORMAT)}
                                                </span>
                                            </li>
                                            <li>
                                                <span className='detailPage__list--primary-label'>{translation('STATUS')}</span>
                                                <span className='detailPage__list--secondary-label'>{result.triggerState}</span>
                                            </li>
                                            <div className='log__raw-record'>
                                                <pre>
                                                    <code>{result.fulljson}</code>
                                                </pre>
                                            </div>
                                        </>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </>
            }
        </>
    );
};

export default ScheduledJobsDetailsPage;